import React, { useRef, useCallback } from 'react';

export type NotebookTabKind = 'notebook' | 'text';

export interface NotebookTab {
  path: string;
  title: string;
  isDirty: boolean;
  kind?: NotebookTabKind; // omitted = 'notebook' for back-compat
}

interface NotebookTabBarProps {
  tabs: NotebookTab[];
  activeTabPath: string | null;
  onSelectTab: (path: string) => void;
  onCloseTab: (path: string) => void;
  onOpenFileExplorer?: () => void;
}

export const NotebookTabBar: React.FC<NotebookTabBarProps> = ({
  tabs,
  activeTabPath,
  onSelectTab,
  onCloseTab,
  onOpenFileExplorer,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent, path: string) => {
    // Middle-click to close
    if (e.button === 1) {
      e.preventDefault();
      onCloseTab(path);
    }
  }, [onCloseTab]);

  if (tabs.length === 0) return null;

  return (
    <div className="flex items-center bg-black/40 border-b border-gray-800/50 flex-shrink-0 min-h-[36px]">
      <div
        ref={scrollRef}
        className="flex-1 flex items-center overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: 'none' }}
      >
        {tabs.map(tab => {
          const isActive = tab.path === activeTabPath;
          return (
            <button
              key={tab.path}
              onClick={() => onSelectTab(tab.path)}
              onMouseDown={(e) => handleMouseDown(e, tab.path)}
              title={tab.path}
              className={`group relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors border-r border-gray-800/30 ${
                isActive
                  ? 'text-white bg-gray-800/60'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
            >
              {/* Dirty indicator */}
              {tab.isDirty && (
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
              )}

              {/* Kind icon — differentiates notebook vs text file */}
              <svg
                className={`w-3 h-3 flex-shrink-0 ${isActive ? 'text-gray-300' : 'text-gray-500'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {tab.kind === 'text' ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>

              {/* Filename */}
              <span className="truncate max-w-[160px]">{tab.title}</span>

              {/* Close button */}
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onCloseTab(tab.path);
                }}
                className={`ml-1 p-0.5 rounded hover:bg-gray-600/50 transition-colors flex-shrink-0 ${
                  isActive ? 'opacity-60 hover:opacity-100' : 'opacity-0 group-hover:opacity-60 hover:!opacity-100'
                }`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </span>

              {/* Active tab bottom border */}
              {isActive && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
              )}
            </button>
          );
        })}
      </div>

      {/* Open file explorer button */}
      {onOpenFileExplorer && (
        <button
          onClick={onOpenFileExplorer}
          title="Open file explorer (Cmd+B)"
          className="p-2 text-gray-600 hover:text-gray-400 transition-colors flex-shrink-0"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}
    </div>
  );
};
